import React from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  IDashboardFolderQuery,
  IDashboardFolderQueryVariables,
  IDashboardMoveMutation,
  IDashboardMoveMutationVariables,
  IDashboardTemplateFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { selectModalData } from '../../../../redux/selectors/modal';
import { assertNotNil } from '../../../../utils/types';
import { closeModal } from '../../../../redux/slices/modal';
import {
  dashboardFolderQuery,
  MOVE_DASHBOARD,
} from '../../../../utils/apollo/dashboards';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import KubitModal from '../../../../components/KubitModal';
import DashboardFolderWidget from '../DashboardFolderWidget';
import {
  dashboardInstanceUpdatePath,
  populatePublishedFolder,
} from '../../../../redux/slices/dashboards/dashboardInstance';
import toastMessage from '../../../../utils/toastMessage';

const DashboardMoveModal = () => {
  const dispatch = useAppDispatch();
  const urlParams = useParams();
  const { dashboardBaseId } = urlParams;

  const dashboard: IDashboardTemplateFieldsFragment =
    useAppSelector(selectModalData);
  assertNotNil(dashboard);
  const { baseId, name, path } = dashboard;

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleLoadFolder = ({ dashboardFolder }: IDashboardFolderQuery) => {
    // Update published folder
    dispatch(populatePublishedFolder({ publishedFolder: dashboardFolder }));
    handleClose();
  };
  const [loadFolder] = useLazyQuery<
    IDashboardFolderQuery,
    IDashboardFolderQueryVariables
  >(dashboardFolderQuery, {
    onCompleted: handleLoadFolder,
  });

  const handleComplete = (
    { dashboardMove }: IDashboardMoveMutation,
    folderId: string | null,
  ) => {
    const { entity, errors } = dashboardMove;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);

    // Request a new published folder
    loadFolder({
      variables: {
        id: folderId,
      },
    });

    if (entity.baseId === dashboardBaseId) {
      // Update instance path
      dispatch(dashboardInstanceUpdatePath({ newPath: entity.path }));
    }

    toastMessage({
      message: `Dashboard ${entity.name} has been moved`,
      type: 'info',
    });
  };

  const [move] = useMutation<
    IDashboardMoveMutation,
    IDashboardMoveMutationVariables
  >(MOVE_DASHBOARD);

  const handleMove = (folderId: string | null) => {
    move({
      variables: {
        input: {
          baseId,
          folderId,
        },
      },
      onCompleted: (data) => handleComplete(data, folderId),
    });
  };

  const defaultFolderId = path[path.length - 1]?.id ?? null;

  return (
    <KubitModal
      open
      onClose={handleClose}
      maxWidth={480}
      noBodyWrapper
      closeOnEscape={false}
    >
      <DashboardFolderWidget
        onSubmit={handleMove}
        onCancel={handleClose}
        dashboardName={name}
        defaultFolderId={defaultFolderId}
        submitLabel={'Move Here'}
        trackContext="dashboards"
        trackAction="move here"
      />
    </KubitModal>
  );
};

export default DashboardMoveModal;
